import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { 
  MapPin, 
  Users, 
  AlertTriangle, 
  FileText, 
  Building2,
  TrendingUp,
  Activity,
  Clock,
  ArrowUpRight
} from 'lucide-react';
import { DashboardStats } from '../types';
import { dashboardService } from '../services';
import { logger } from '../utils';
import { format } from 'date-fns';

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await dashboardService.getStats();
      setStats(data);
    } catch (err: any) {
      logger.apiError('/admin/stats', err, { component: 'DashboardPage', action: 'loadStats' });
      setError(err.response?.data?.message || 'Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      name: 'Total Strayyos',
      value: stats?.totalStrayyos || 0,
      change: '+12%',
      changeType: 'positive',
      icon: MapPin,
      href: '/strayyos'
    },
    {
      name: 'Active Angels',
      value: stats?.totalAngels || 0,
      change: '+8%',
      changeType: 'positive',
      icon: Users,
      href: '/angels'
    },
    {
      name: 'Active Emergencies',
      value: stats?.activeEmergencies || 0,
      change: '-2%',
      changeType: 'negative',
      icon: AlertTriangle,
      href: '/emergencies'
    },
    {
      name: 'Pending Claims',
      value: stats?.pendingClaims || 0,
      change: '+5%',
      changeType: 'positive',
      icon: FileText,
      href: '/claims'
    },
    {
      name: 'Pending Reports',
      value: stats?.pendingReports || 0,
      change: '+3%',
      changeType: 'positive',
      icon: FileText,
      href: '/reports'
    },
    {
      name: 'Partners',
      value: stats?.totalPartners || 0,
      change: '+1%',
      changeType: 'positive',
      icon: Building2,
      href: '/partners'
    },
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-green-600 bg-green-100';
    }
  };

  if (loading) {
    return (
      <Layout currentPage="dashboard">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout currentPage="dashboard">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Overview of your Strayyo platform
          </p>
        </div>

        {error && (
          <div className="mb-6 rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
          {statCards.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.name}
                className="relative bg-white pt-5 px-4 pb-12 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => window.location.href = item.href}
              >
                <dt>
                  <div className="absolute bg-orange-500 rounded-md p-3">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <p className="ml-16 text-sm font-medium text-gray-500 truncate">
                    {item.name}
                  </p>
                </dt>
                <dd className="ml-16 pb-6 flex items-baseline sm:pb-7">
                  <p className="text-2xl font-semibold text-gray-900">
                    {item.value.toLocaleString()}
                  </p>
                  <p className={`ml-2 flex items-baseline text-sm font-semibold ${
                    item.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {item.changeType === 'positive' ? (
                      <TrendingUp className="self-center flex-shrink-0 h-4 w-4 text-green-500" />
                    ) : (
                      <TrendingUp className="self-center flex-shrink-0 h-4 w-4 text-red-500 transform rotate-180" />
                    )}
                    <span className="ml-1">{item.change}</span>
                  </p>
                  <div className="absolute bottom-0 inset-x-0 bg-gray-50 px-4 py-4 sm:px-6">
                    <div className="text-sm">
                      <a href={item.href} className="font-medium text-orange-600 hover:text-orange-500">
                        View all<span className="sr-only"> {item.name}</span>
                      </a>
                    </div>
                  </div>
                </dd>
              </div>
            );
          })}
        </div>

        {/* Recent Activity */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Recent Activity
              </h3>
              <Activity className="h-5 w-5 text-gray-400" />
            </div>
            
            {stats?.recentActivity && stats.recentActivity.length > 0 ? (
              <div className="flow-root">
                <ul className="-mb-8">
                  {stats.recentActivity.map((activity, activityIdx) => (
                    <li key={activity.id}>
                      <div className="relative pb-8">
                        {activityIdx !== stats.recentActivity.length - 1 ? (
                          <span
                            className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                            aria-hidden="true"
                          />
                        ) : null}
                        <div className="relative flex items-start space-x-3">
                          <div className="relative">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ring-8 ring-white ${getPriorityColor(activity.priority)}`}>
                              <Clock className="h-5 w-5" />
                            </div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div>
                              <div className="text-sm">
                                <p className="font-medium text-gray-900">
                                  {activity.description}
                                </p>
                              </div>
                              <p className="mt-0.5 text-sm text-gray-500">
                                {format(new Date(activity.timestamp), 'MMM d, yyyy at h:mm a')}
                              </p>
                            </div>
                          </div>
                          <div className="flex-shrink-0 self-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getPriorityColor(activity.priority)}`}>
                              {activity.priority}
                            </span>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="text-center py-12">
                <Activity className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No recent activity</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Activity will appear here as it happens.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <a
            href="/strayyos"
            className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-orange-500 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div>
              <span className="rounded-lg inline-flex p-3 bg-orange-50 text-orange-700 ring-4 ring-white">
                <MapPin className="h-6 w-6" />
              </span>
            </div>
            <div className="mt-8">
              <h3 className="text-lg font-medium">
                <span className="absolute inset-0" aria-hidden="true" />
                Manage Strayyos
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                View and update strayyo information, photos, and status.
              </p>
            </div>
            <span
              className="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
              aria-hidden="true"
            >
              <ArrowUpRight className="h-6 w-6" />
            </span>
          </a>

          <a
            href="/angels"
            className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-orange-500 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div>
              <span className="rounded-lg inline-flex p-3 bg-orange-50 text-orange-700 ring-4 ring-white">
                <Users className="h-6 w-6" />
              </span>
            </div>
            <div className="mt-8">
              <h3 className="text-lg font-medium">
                <span className="absolute inset-0" aria-hidden="true" />
                Manage Angels
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Verify angels and manage their assignments.
              </p>
            </div>
            <span
              className="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
              aria-hidden="true"
            >
              <ArrowUpRight className="h-6 w-6" />
            </span>
          </a>

          <a
            href="/emergencies"
            className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-orange-500 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div>
              <span className="rounded-lg inline-flex p-3 bg-red-50 text-red-700 ring-4 ring-white">
                <AlertTriangle className="h-6 w-6" />
              </span>
            </div>
            <div className="mt-8">
              <h3 className="text-lg font-medium">
                <span className="absolute inset-0" aria-hidden="true" />
                Handle Emergencies
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Review and escalate emergency cases.
              </p>
            </div>
            <span
              className="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
              aria-hidden="true"
            >
              <ArrowUpRight className="h-6 w-6" />
            </span>
          </a>

          <a
            href="/claims"
            className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-orange-500 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div>
              <span className="rounded-lg inline-flex p-3 bg-blue-50 text-blue-700 ring-4 ring-white">
                <FileText className="h-6 w-6" />
              </span>
            </div>
            <div className="mt-8">
              <h3 className="text-lg font-medium">
                <span className="absolute inset-0" aria-hidden="true" />
                Review Claims
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Approve or reject angel claim requests.
              </p>
            </div>
            <span
              className="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
              aria-hidden="true"
            >
              <ArrowUpRight className="h-6 w-6" />
            </span>
          </a>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
